import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Subscription } from "rxjs";
import Constants from "src/app/constants/constants";
import LocalizationConstants from "src/app/constants/localization-constants";
import { FilterCategory, ViewType } from "src/app/models/Enums";
import { ToolFilterCategory } from "src/app/models/Filters/ToolFilterCategory";
import { ToolFilterOption } from "src/app/models/Filters/ToolFilterOption";
import { ToolFilterStateItem } from "src/app/models/Filters/ToolFilterStateItem";
import { ToolFilter } from "src/app/models/ToolFilter";
import { ToolService } from "src/app/services/tool.service";
import { ToolFilterService } from "src/app/services/tool-filter.service";

@Component({
  selector: "filter-state-chips",
  templateUrl: "./filter-state-chips.component.html",
  styleUrls: ["./filter-state-chips.component.scss"],
  standalone: false,
})
export class FilterStateChipsComponent implements OnInit, OnDestroy {
  private readonly _subscriptions$: Subscription[] = [];
  aggregationCategories: ToolFilterCategory[] = [];

  @Input()
  set aggregations(value) {
    this.aggregationCategories = value;
    if (value) this.setFilterItems();
  }

  @Output() filterOpen: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() clearAll: EventEmitter<boolean> = new EventEmitter<boolean>();

  enabledFilters: ToolFilterStateItem[] = [];
  localizationConstants = LocalizationConstants;

  constructor(
    private toolFilterService: ToolFilterService,
    private toolService: ToolService,
  ) {}

  ngOnInit() {
    this._subscriptions$.push(this.onFilterChange());
  }

  private setFilterItems() {
    const filter = this.toolFilterService.get(ViewType.ToolsAll);
    this.enabledFilters = this.getStateItems(filter);
  }

  onFilterChange() {
    return this.toolFilterService.toolFilterChange.subscribe((filter) => {
      this.enabledFilters = this.getStateItems(filter);
    });
  }

  getStateItems(filter: ToolFilter): ToolFilterStateItem[] {
    const items: ToolFilterStateItem[] = [];

    const categoryItems = filter.columnFilters.map((a) => {
      const category = this.aggregationCategories.find((category) => category.id === a.property);
      if (!category) return [];
      return a.values.map((b) => new ToolFilterStateItem(category.title, category.id, b, b));
    });

    // Update labels for Statuses
    categoryItems.forEach((perCategory) =>
      perCategory.forEach((item) => {
        if (item.id !== FilterCategory.Status) return;
        const status = this.toolService.statuses.find((status) => status.value === item.value);

        // TODO: temp solution in case we have for some reason not mapped eventStatus
        item.valueTitle = status ? status.label : Constants.UNKNOWN_EVENT_STATUS;
      }),
    );

    categoryItems.forEach((perCategory) => items.push(...perCategory));

    // Include boolean category = IsActive
    filter.booleanFilters?.forEach((a) => {
      const category = this.getCategory(FilterCategory.IsActive);
      if (!category) return;
      const option = this.getMatchingOption(FilterCategory.IsActive, a.toString());
      items.push(
        new ToolFilterStateItem(
          category.title,
          FilterCategory.IsActive,
          option.value,
          option.title,
        ),
      );
    });

    return items;
  }

  private getCategory(id: string): ToolFilterCategory {
    return this.aggregationCategories.find((category) => category.id === id);
  }

  private getMatchingOption(id: FilterCategory, value: string): ToolFilterOption {
    return (
      this.getCategory(id)?.options.find(
        (a) => a.value.toLowerCase() === value.toLowerCase(),
        // in case there is no tool in the selected company, but there are some filters
        // left from previous company, we need to have a default filter option, since
        // there won't be any valid options returned from the BE, because there are no tools.
      ) || new ToolFilterOption()
    );
  }

  onFilterItemRemove(filterItem: ToolFilterStateItem) {
    let filter = this.toolFilterService.get(ViewType.ToolsAll);

    if (filterItem.isBoolean) {
      filter = this.removeIsActiveFilter(filterItem, filter);
    } else {
      filter = this.removeColumnFilter(filterItem, filter);
    }

    this.toolFilterService.set(filter);
  }

  removeIsActiveFilter(filterItem: ToolFilterStateItem, filter: ToolFilter): ToolFilter {
    filter.booleanFilters = filter.booleanFilters.filter(
      (a) => filterItem.value.toLowerCase() !== a.toString(),
    );

    if (filter.booleanFilters?.length === 0) filter.booleanFilters = null;

    return filter;
  }

  removeColumnFilter(filterItem: ToolFilterStateItem, filter: ToolFilter): ToolFilter {
    const categoryIndex = filter.columnFilters.findIndex((a) => a.property === filterItem.id);

    const category = filter.columnFilters[categoryIndex];
    category.values = category.values.filter((a) => a !== filterItem.value);

    if (category.values?.length === 0) {
      filter.columnFilters.splice(categoryIndex, 1);
    }

    return filter;
  }

  onClearAll() {
    this.toolFilterService.resetFilters(ViewType.ToolsAll);
  }

  ngOnDestroy() {
    this._subscriptions$.forEach((x) => x.unsubscribe());
  }
}
